import React, { useState } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Autocomplete,
  Box,
  Divider,
  Paper,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { SectionHeader } from '../AddPatientStyles';

const StepIdentity = ({
  formData,
  handleChange,
  errors,
  clientOptions,
  searchLoading,
  setSearchQuery,
  handleSelectExistingClient,
  photoFile,
  setPhotoFile,
}) => {
  const [selectedPreviewClient, setSelectedPreviewClient] = useState(null);

  const handleAutocompleteChange = (event, newValue) => {
    setSelectedPreviewClient(newValue);
  };

  const handleConfirmApply = () => {
    if (selectedPreviewClient) {
      handleSelectExistingClient(null, selectedPreviewClient);
      setSelectedPreviewClient(null);
    }
  };

  return (
    <Box>
      <SectionHeader
        title="Rechercher un client existant"
        description="Sélectionnez un client pour pré-remplir automatiquement ses informations"
      />

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sx={{ width: '100% !important', maxWidth: '100% !important', flexBasis: '100% !important', }}>
          <Autocomplete
            options={clientOptions}
            loading={searchLoading}
            value={selectedPreviewClient}
            getOptionLabel={(option) =>
              `${option.prenom_client || option.prenom || ''} ${
                option.nom_client || option.nom || ''
              } (${option.email_client || option.email || ''})`
            }
            onInputChange={(event, newInputValue) =>
              setSearchQuery(newInputValue)
            }
            onChange={handleAutocompleteChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Rechercher par email ou nom..."
                placeholder="Tapez pour chercher..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#FAFBFC',
                  },
                }}
              />
            )}
          />
        </Grid>

        {selectedPreviewClient && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                backgroundColor: '#F4F7FE',
                borderRadius: '12px',
                border: '1px solid #E0E5F2',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#2B3674', fontWeight: 'bold', mb: 0.5 }}>
                  Client trouvé : {selectedPreviewClient.prenom_client || selectedPreviewClient.prenom} {selectedPreviewClient.nom_client || selectedPreviewClient.nom}
                </Typography>
                <Typography variant="body2" sx={{ color: '#A3AED0' }}>
                  Email : {selectedPreviewClient.email_client || selectedPreviewClient.email || 'Non renseigné'}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={handleConfirmApply}
                sx={{
                  backgroundColor: '#4318FF',
                  '&:hover': { backgroundColor: '#3311DB' },
                  borderRadius: '8px',
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                }}
              >
                Confirmer et auto-remplir
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />
      <SectionHeader title="État civil & contacts" description="Informations permettant d'identifier le patient" />
      
      {/* Si le client est déjà attaché, on peut afficher un petit badge informatif ici aussi si besoin */}
      {formData.isExistingClient && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#E6FFFA', border: '1px solid #B2F5EA', borderRadius: '8px' }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#234E52', fontWeight: 600 }}>
            ✨ Ce dossier est rattaché au client existant : {formData.prenom_patient} {formData.nom_patient} ({formData.email_patient})
          </Typography>
        </Paper>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Nom du patient *"
            name="nom_patient"
            value={formData.nom_patient}
            onChange={handleChange}
            error={!!errors.nom_patient}
            helperText={errors.nom_patient}
            required
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Prénom du patient *"
            name="prenom_patient"
            value={formData.prenom_patient}
            onChange={handleChange}
            error={!!errors.prenom_patient}
            helperText={errors.prenom_patient}
            required
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Email"
            name="email_patient"
            type="email"
            value={formData.email_patient}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Téléphone"
            name="telephone_patient"
            value={formData.telephone_patient}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Lieu de résidence"
            name="lieu_residence_patient"
            value={formData.lieu_residence_patient}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Date de naissance"
            name="date_naissance_patient"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.date_naissance_patient}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Situation familiale"
            name="situation_familiale_patient"
            value={formData.situation_familiale_patient}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Profession"
            name="profession_patient"
            value={formData.profession_patient}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default StepIdentity;